import React from 'react';
import { motion } from 'framer-motion';
import { 
  HeartIcon, 
  ShieldCheckIcon, 
  ClockIcon,
  DocumentTextIcon 
} from '@heroicons/react/24/outline';

const AiDoctor = () => {
  const features = [
    {
      name: 'Symptom Analysis',
      description: 'Describe your symptoms and get instant AI-powered insights about potential causes and recommended actions.',
      icon: HeartIcon,
    },
    {
      name: 'Medical History',
      description: 'Maintain comprehensive health records with intelligent analysis and trend identification over time.',
      icon: DocumentTextIcon,
    },
    {
      name: '24/7 Availability',
      description: 'Health concerns don\'t follow business hours. Get immediate AI assistance anytime, anywhere, completely offline.',
      icon: ClockIcon,
    },
    {
      name: 'HIPAA Compliant',
      description: 'Your medical data stays on your device. Complete privacy and security with offline processing.',
      icon: ShieldCheckIcon,
    },
  ];

  const capabilities = [
    'Initial symptom assessment',
    'Medication information and interactions',
    'Preventive care recommendations',
    'Health risk factor analysis',
    'Lifestyle and wellness guidance',
    'Emergency situation identification',
  ];

  return (
    <div id="ai-doctor" className="bg-gradient-to-br from-dark-900 to-dark-950 py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-base font-semibold leading-7 text-primary-500">AI Doctor</h2>
          <p className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Your Personal Health Assistant
          </p>
          <p className="mt-6 text-lg leading-8 text-primary-200">
            Revolutionary AI-powered medical assistance that works completely offline. Get instant health insights 
            while maintaining complete privacy of your medical information.
          </p>
        </div>
        
        <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-2">
            <div>
              <h3 className="text-xl font-semibold text-white mb-8">Key Features</h3>
              <dl className="space-y-16">
                {features.map((feature) => (
                  <div key={feature.name} className="flex flex-col">
                    <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-white">
                      <feature.icon className="h-8 w-8 flex-none text-green-500" aria-hidden="true" />
                      {feature.name}
                    </dt>
                    <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-green-200">
                      <p className="flex-auto">{feature.description}</p>
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
            
            <div className="bg-black/50 backdrop-blur-sm rounded-lg p-8 border border-green-900/20">
              <h3 className="text-xl font-semibold text-white mb-6">AI Doctor Capabilities</h3>
              <ul className="space-y-3">
                {capabilities.map((capability) => (
                  <li key={capability} className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-500/20">
                        <div className="h-2 w-2 rounded-full bg-primary-500"></div>
                      </div>
                    </div>
                    <p className="ml-3 text-base text-primary-200">{capability}</p>
                  </li>
                ))}
              </ul>
              
              <div className="mt-8 p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
                <p className="text-sm text-red-400">
                  <strong>Important:</strong> AI Doctor is for informational purposes only and should not replace 
                  professional medical advice. Always consult with qualified healthcare providers for medical decisions.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-20 text-center">
          <div className="rounded-lg bg-gradient-to-r from-primary-600 to-primary-700 p-8 text-white border border-primary-500/20">
            <h3 className="text-2xl font-bold mb-4">Take Control of Your Health</h3>
            <p className="text-primary-100 mb-6">
              Experience the future of personal health management with AI Doctor - completely private, offline, and always available.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <motion.a
                href="https://play.google.com/store/apps/details?id=com.speedrai.app"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-6 py-3 bg-black rounded-lg hover:bg-gray-800 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <svg className="w-6 h-6 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zM16.8 15.099l2.302 2.302a1 1 0 00.448-.833V7.432a1 1 0 00-.448-.833l-2.302 2.302 2.3 2.298zM5.864 21.342l10.937 6.333-8.635-8.635-2.302 2.302z"/>
                </svg>
                <span className="text-white font-medium">Get it on Google Play</span>
              </motion.a>
              
              <motion.a
                href="https://apps.apple.com/us/app/speedr-ai-run-without-internet/id6743132806"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-6 py-3 bg-black rounded-lg hover:bg-gray-800 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <svg className="w-6 h-6 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <span className="text-white font-medium">Download on App Store</span>
              </motion.a>
            </div>
            
            <div className="flex gap-4 justify-center">
              <button className="rounded-md bg-dark-900 px-8 py-3 text-base font-semibold text-primary-400 shadow-sm hover:bg-dark-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 border border-primary-500/30">
                Download AI Doctor
              </button>
              <button className="rounded-md px-8 py-3 text-base font-semibold text-white border border-primary-500/30 hover:bg-primary-500/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500">
                Learn More
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiDoctor;
